import { FC, useRef, useState, useEffect } from "react";
import cx from "classnames";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import SwiperType from "swiper/types/swiper-class";
import { Member, useAllGroupMembersQuery } from "@/fetch/social";
import { useCurrentTrip, useDebounce } from "@/hooks";
import { IconButton } from "../Button";
import Divider from "@/components/Divider";
import { ArrowIcon } from "@/components/Icon";
import Typography from "@/components/Typography";
import uniqBy from "lodash/uniqBy";
import { SearchTextField } from "@/components/TextField";
import List from "@/components/List";
import NewConversationItem, {
  NewConversationItemLoader,
} from "./NewConversationItem";
import { useSwiperSlidesPerView } from "@/hooks";
import { SocialAvatar } from "@/components/Social";
import { useTwilioConversation } from "./hooks";
import compact from "lodash/compact";
import { EmptySearchResult } from "@/components/EmptyList";
import { TripStatus } from "@/fetch/gworld/useTripsQuery";
import styles from "./SuggestionList.module.scss";

type Props = {
  //
};

const SuggestionList: FC<Props> = () => {
  const [firstSwiperObj, setFirstSwiperObj] = useState<SwiperType | undefined>(
    undefined
  );
  const { currentTrip } = useCurrentTrip();
  const thisUserCid = currentTrip?.cid;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 500);
  const containerRef = useRef<HTMLDivElement>(null);

  const isFilterSet = debouncedSearchTerm !== "";
  const { slidesPerView: firstSlidesPerView } = useSwiperSlidesPerView({
    containerRef,
    slideWidth: 92,
  });

  const {
    data: membersData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useAllGroupMembersQuery(
    isFilterSet
      ? {
          pageNum: 1,
          pageSize: 100,
          searchParam: debouncedSearchTerm,
        }
      : undefined
  );
  const { openChatPopup, setOverlayLoading } = useTwilioConversation();
  const isTripCompleted = currentTrip?.status === TripStatus.Complete;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersData]);

  const membersList = compact(
    uniqBy(
      membersData?.pages?.map((page) => page?.results)?.flat(1) || [],
      "cid"
    ).filter((each) => each?.cid !== thisUserCid)
  );

  const onItemClick = async (member: Member) => {
    try {
      setOverlayLoading?.(true);
      await openChatPopup(member?.cid, "New Message Page");
    } catch (error) {
      console.error(error);
    } finally {
      setOverlayLoading?.(false);
    }
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <Swiper
        className={cx(styles.swiper, styles.categories)}
        modules={[Navigation]}
        // navigation
        slidesPerView={firstSlidesPerView}
        initialSlide={0}
        spaceBetween={16}
        onSwiper={setFirstSwiperObj}
      >
        {membersList?.map((member) => (
          <SwiperSlide
            onClick={() => onItemClick(member)}
            key={member.cid}
            className={cx(styles.memberContainer, {})}
          >
            <SocialAvatar
              cloudinaryPath={member.s3_profile_pic}
              className={styles.avatar}
            />

            <Typography variant="body2" className={styles.name}>
              {member.nickname}
            </Typography>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export default SuggestionList;
